import { useEffect, useMemo, useRef, useState } from 'react';
import { api, ApiError } from '../api';
import {
  daysAgo,
  formatCompact,
  formatDateTime,
  formatNumber,
  formatPercent,
  formatSpan,
  formatWait,
  toDateInput,
} from '../format';
import {
  CHANNELS,
  CHANNEL_LABELS,
  type Channel,
  type LocationSummary,
  type MessageFilterState,
  type MessageSummaryResponse,
  type MessagesResponse,
  type PendingReply,
} from '../types';
import { StatTile } from '../components/StatTile';
import { StackedColumnChart } from '../components/StackedColumnChart';
import { HorizontalBarChart, type BarDatum } from '../components/HorizontalBarChart';
import { DateRangeField, FilterPanel, SwitchField } from '../components/FilterPanel';
import { TagFilter, type TagOption } from '../components/TagFilter';

export const DEFAULT_MESSAGE_FILTERS: MessageFilterState = {
  from: daysAgo(29),
  to: toDateInput(new Date()),
  locationIds: [],
  userIds: [],
  channels: [],
  direction: '',
  sender: '',
  search: '',
  activeClientsOnly: true,
  tags: [],
  tagMatch: 'any',
  minWaitHours: '',
};

const PRESETS = [
  { label: 'Today', days: 0 },
  { label: '7d', days: 6 },
  { label: '30d', days: 29 },
  { label: '90d', days: 89 },
];

const WAITS: { value: string; label: string }[] = [
  { value: '', label: 'Any wait' },
  { value: '1', label: '1 hour+' },
  { value: '4', label: '4 hours+' },
  { value: '24', label: '1 day+' },
  { value: '72', label: '3 days+' },
];

/** Bottom first: what came in, what people sent, what automations sent. */
const DAY_SERIES = [
  { key: 'inbound', label: 'Received', color: 'var(--outcome-voicemail)' },
  { key: 'person', label: 'Sent by people', color: 'var(--outcome-answered)' },
  { key: 'automated', label: 'Sent by automations', color: 'var(--outcome-pending)' },
];

function relativeChange(current: number, previous: number) {
  if (previous === 0) return current === 0 ? 0 : null;
  return (current - previous) / previous;
}

function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

/**
 * Message tracking: what came in and went out on every channel but calls,
 * who sent it, how fast people answered, and — first on the page — every
 * contact still waiting for a reply.
 */
export function Messages({
  filters,
  onFiltersChange,
  locations,
  tagOptions,
  dataVersion,
}: {
  filters: MessageFilterState;
  onFiltersChange: (next: MessageFilterState) => void;
  locations: LocationSummary[];
  tagOptions: TagOption[];
  dataVersion: number;
}) {
  const debounced = useDebounced(filters, 250);
  const [summary, setSummary] = useState<MessageSummaryResponse | null>(null);
  const [pending, setPending] = useState<{ pending: PendingReply[]; total: number } | null>(null);
  const [list, setList] = useState<MessagesResponse | null>(null);
  const [senders, setSenders] = useState<
    { userId: string | null; userName: string | null; locationId: string; total: number }[]
  >([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<'pending' | 'all'>('pending');

  useEffect(() => setPage(1), [debounced]);

  // A filter change dims the page; new data from a sync just swaps in.
  const lastFilters = useRef<MessageFilterState | null>(null);
  useEffect(() => {
    let cancelled = false;
    if (lastFilters.current !== debounced) setLoading(true);
    lastFilters.current = debounced;
    Promise.all([api.messageSummary(debounced), api.pendingReplies(debounced), api.messageSenders(debounced)])
      .then(([summaryResponse, pendingResponse, senderResponse]) => {
        if (cancelled) return;
        setSummary(summaryResponse);
        setPending(pendingResponse);
        setSenders(senderResponse.senders);
        setError(null);
      })
      .catch((cause) => !cancelled && setError(cause instanceof ApiError ? cause.message : String(cause)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [debounced, dataVersion]);

  useEffect(() => {
    if (view !== 'all') return;
    let cancelled = false;
    api
      .messages(debounced, page, pageSize)
      .then((response) => !cancelled && setList(response))
      .catch((cause) => !cancelled && setError(cause instanceof ApiError ? cause.message : String(cause)));
    return () => {
      cancelled = true;
    };
  }, [view, debounced, page, pageSize, dataVersion]);

  const today = toDateInput(new Date());
  const visibleLocations = filters.activeClientsOnly
    ? locations.filter((location) => location.isActiveClient)
    : locations;
  const activeCount =
    (filters.locationIds.length ? 1 : 0) +
    (filters.userIds.length ? 1 : 0) +
    (filters.channels.length ? 1 : 0) +
    (filters.direction ? 1 : 0) +
    (filters.sender ? 1 : 0) +
    (filters.tags.length ? 1 : 0) +
    (filters.search.trim() ? 1 : 0) +
    (filters.minWaitHours ? 1 : 0) +
    (filters.activeClientsOnly ? 0 : 1);

  function toggleChannel(channel: Channel) {
    const channels = filters.channels.includes(channel)
      ? filters.channels.filter((item) => item !== channel)
      : [...filters.channels, channel];
    onFiltersChange({ ...filters, channels });
  }

  const userBars = useMemo<BarDatum[]>(() => {
    if (!summary) return [];
    const rows = summary.byUser.filter((row) => row.userId).slice(0, 12);
    // One person can send from several sub-accounts: name the sub-account when a name repeats.
    const counts = new Map<string, number>();
    for (const row of rows) counts.set(row.userName ?? row.userId!, (counts.get(row.userName ?? row.userId!) ?? 0) + 1);
    return rows.map((row) => ({
      key: `${row.userId}-${row.locationId}`,
      label:
        (counts.get(row.userName ?? row.userId!) ?? 0) > 1 && row.locationName
          ? `${row.userName ?? row.userId} · ${row.locationName}`
          : (row.userName ?? row.userId!),
      sublabel: row.locationName ?? undefined,
      value: row.sent,
      tooltipRows: [
        { label: 'Sent', value: formatNumber(row.sent) },
        { label: 'By hand', value: formatNumber(row.person) },
        { label: 'By automations', value: formatNumber(row.automated) },
        { label: 'Contacts', value: formatNumber(row.contacts) },
      ],
    }));
  }, [summary]);

  const channelBars = useMemo<BarDatum[]>(() => {
    if (!summary) return [];
    return summary.byChannel.map((row) => ({
      key: row.channel,
      label: CHANNEL_LABELS[row.channel] ?? row.channel,
      value: row.total,
      tooltipRows: [
        { label: 'Received', value: formatNumber(row.inbound) },
        { label: 'Sent', value: formatNumber(row.outbound) },
      ],
    }));
  }, [summary]);

  const totals = summary?.totals;

  return (
    <div className="stack">
      <FilterPanel activeCount={activeCount} onReset={() => onFiltersChange(DEFAULT_MESSAGE_FILTERS)}>
        <DateRangeField
          id="messages-from"
          label="Date range"
          from={filters.from}
          to={filters.to}
          max={today}
          presets={PRESETS.map((preset) => ({
            label: preset.label,
            from: preset.days === 0 ? today : daysAgo(preset.days),
            to: today,
          }))}
          onChange={(from, to) => onFiltersChange({ ...filters, from, to })}
        />
        <SwitchField
          id="messages-active"
          label="Clients"
          text="Active only"
          checked={filters.activeClientsOnly}
          onChange={(checked) => onFiltersChange({ ...filters, activeClientsOnly: checked, locationIds: [] })}
        />
        <div className="field">
          <label htmlFor="messages-location">Sub-account</label>
          <select
            id="messages-location"
            value={filters.locationIds[0] ?? ''}
            onChange={(event) =>
              onFiltersChange({ ...filters, locationIds: event.target.value ? [event.target.value] : [], userIds: [] })
            }
          >
            <option value="">{filters.activeClientsOnly ? 'All active clients' : 'All sub-accounts'}</option>
            {visibleLocations.map((location) => (
              <option value={location.id} key={location.id}>
                {location.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="messages-user">Team member</label>
          <select
            id="messages-user"
            value={filters.userIds[0] ?? ''}
            onChange={(event) =>
              onFiltersChange({ ...filters, userIds: event.target.value ? [event.target.value] : [] })
            }
          >
            <option value="">Anyone</option>
            <option value="unassigned">No team member</option>
            {senders
              .filter((sender) => sender.userId)
              .map((sender) => (
                <option value={sender.userId!} key={`${sender.userId}-${sender.locationId}`}>
                  {sender.userName ?? sender.userId} ({sender.total})
                </option>
              ))}
          </select>
        </div>
        <div className="field">
          <label>Direction</label>
          <div className="segmented" role="group" aria-label="Direction">
            {(
              [
                ['', 'Both'],
                ['inbound', 'Received'],
                ['outbound', 'Sent'],
              ] as [MessageFilterState['direction'], string][]
            ).map(([value, label]) => (
              <button
                type="button"
                key={value || 'both'}
                aria-pressed={filters.direction === value}
                onClick={() => onFiltersChange({ ...filters, direction: value })}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="field">
          <label>Sent by</label>
          <div className="segmented" role="group" aria-label="Sent by">
            {(
              [
                ['', 'Anyone'],
                ['person', 'People'],
                ['automated', 'Automations'],
              ] as [MessageFilterState['sender'], string][]
            ).map(([value, label]) => (
              <button
                type="button"
                key={value || 'anyone'}
                aria-pressed={filters.sender === value}
                onClick={() => onFiltersChange({ ...filters, sender: value })}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="field field-grow">
          <label htmlFor="messages-search">Search</label>
          <input
            id="messages-search"
            type="search"
            placeholder="Contact or message text…"
            value={filters.search}
            onChange={(event) => onFiltersChange({ ...filters, search: event.target.value })}
          />
        </div>
        <div className="field">
          <label>Channel</label>
          <div className="chips">
            {CHANNELS.map((channel) => (
              <button
                type="button"
                className="chip"
                key={channel}
                aria-pressed={filters.channels.includes(channel)}
                onClick={() => toggleChannel(channel)}
              >
                {CHANNEL_LABELS[channel]}
              </button>
            ))}
          </div>
        </div>
        <div className="field">
          <label htmlFor="messages-wait">Waiting</label>
          <select
            id="messages-wait"
            value={filters.minWaitHours}
            onChange={(event) => onFiltersChange({ ...filters, minWaitHours: event.target.value })}
          >
            {WAITS.map((wait) => (
              <option key={wait.value} value={wait.value}>
                {wait.label}
              </option>
            ))}
          </select>
        </div>
        <TagFilter
          id="messages-tags"
          options={tagOptions}
          selected={filters.tags}
          match={filters.tagMatch}
          onChange={(tags, tagMatch) => onFiltersChange({ ...filters, tags, tagMatch })}
        />
      </FilterPanel>

      {error ? <div className="banner error">{error}</div> : null}

      {!summary || !totals ? (
        <p className="empty">{loading ? 'Loading messages…' : 'No messages yet — run a sync.'}</p>
      ) : (
        <div className={loading ? 'stack reloading' : 'stack'}>
          <div className="grid grid-kpi">
            <StatTile
              label="Pending replies"
              value={formatCompact(totals.pendingReplies)}
              hero
              footnote={
                totals.pendingReplies
                  ? `${formatNumber(totals.pendingMessages)} unanswered messages · longest wait ${formatWait(totals.oldestPendingAt)}`
                  : 'Nobody is waiting for an answer'
              }
            />
            <StatTile
              label="Messages"
              value={formatCompact(totals.messages)}
              delta={(() => {
                const change = relativeChange(totals.messages, summary.previous.total);
                return change === null ? null : { change, higherIsBetter: true };
              })()}
              footnote={`${formatNumber(totals.contacts)} contacts`}
            />
            <StatTile
              label="Received"
              value={formatCompact(totals.inbound)}
              delta={(() => {
                const change = relativeChange(totals.inbound, summary.previous.inbound);
                return change === null ? null : { change, higherIsBetter: true };
              })()}
              footnote={`from ${formatNumber(totals.contactsWhoWrote)} contacts`}
            />
            <StatTile
              label="Sent by people / automations"
              compact
              value={`${formatCompact(totals.sentByPeople)} / ${formatCompact(totals.automated)}`}
              footnote={`${formatNumber(totals.failed)} failed to deliver`}
            />
            <StatTile
              label="Median response time"
              value={formatSpan(totals.medianResponseMs)}
              footnote={`Average ${formatSpan(totals.averageResponseMs)} · ${formatNumber(totals.conversationsAnswered)} of ${formatNumber(totals.conversationsStarted)} answered`}
            />
            <StatTile
              label="Answered within 1 hour"
              value={
                totals.conversationsStarted
                  ? formatPercent(totals.answeredWithin1h / totals.conversationsStarted, 0)
                  : '—'
              }
              footnote={
                totals.conversationsStarted
                  ? `${formatPercent(totals.answeredWithin24h / totals.conversationsStarted, 0)} within a day`
                  : 'No messages received in this range'
              }
            />
          </div>

          <div className="card">
            <div className="card-head">
              <h2>
                {view === 'pending' ? 'Waiting for a reply' : 'All messages'}{' '}
                <span className="small muted">
                  {view === 'pending'
                    ? formatNumber(pending?.total ?? 0)
                    : formatNumber(list?.total ?? totals.messages)}
                </span>
              </h2>
              <div className="row">
                <div className="segmented" role="group" aria-label="List">
                  <button type="button" aria-pressed={view === 'pending'} onClick={() => setView('pending')}>
                    Pending replies
                  </button>
                  <button type="button" aria-pressed={view === 'all'} onClick={() => setView('all')}>
                    All messages
                  </button>
                </div>
                <a
                  className="btn btn-sm"
                  href={view === 'pending' ? api.pendingRepliesCsvUrl(debounced) : api.messagesCsvUrl(debounced)}
                >
                  Export CSV
                </a>
              </div>
            </div>
            {view === 'pending' ? (
              <>
                <p className="card-sub">
                  Contacts whose latest message (received in the date range) has had no answer from a person since. A
                  workflow or campaign message does not count as an answer; a call does. Opt-outs such as “STOP” are
                  left out. Longest wait first.
                </p>
                <div className="chips wait-chips" aria-label="How long they have waited">
                  {summary.pendingWait.map((bucket) => (
                    <span className="chip chip-static" key={bucket.key}>
                      {bucket.label} <strong>{formatNumber(bucket.count)}</strong>
                    </span>
                  ))}
                </div>
                <PendingTable rows={pending?.pending ?? []} total={pending?.total ?? 0} />
              </>
            ) : (
              <MessageTable
                data={list}
                page={page}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setPage(1);
                }}
              />
            )}
          </div>

          <div className="card">
            <div className="card-head">
              <h2>Messages per day</h2>
            </div>
            <p className="card-sub">
              Received, sent by a person, and sent by workflows or campaigns. Calls are on the Calls tab.
            </p>
            <StackedColumnChart
              data={summary.timeseries.map((point) => ({
                ...point,
                total: point.inbound + point.person + point.automated,
              }))}
              series={DAY_SERIES}
              ariaLabel="Messages per day"
            />
          </div>

          <div className="grid grid-2">
            <div className="card">
              <div className="card-head">
                <h2>By channel</h2>
              </div>
              <p className="card-sub">Click a channel to filter to it.</p>
              <HorizontalBarChart
                data={channelBars}
                onSelect={(channel) => onFiltersChange({ ...filters, channels: [channel as Channel] })}
              />
            </div>
            <div className="card">
              <div className="card-head">
                <h2>Sent by team member</h2>
                <span className="small muted">Top 12</span>
              </div>
              <p className="card-sub">
                Messages HighLevel tied to a team member, by hand or by a workflow sending in their name.
              </p>
              <HorizontalBarChart data={userBars} labelGutter={210} />
            </div>
          </div>

          <div className="card">
            <div className="card-head">
              <h2>By sub-account</h2>
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Sub-account</th>
                    <th className="num">Messages</th>
                    <th className="num">Received</th>
                    <th className="num">Sent by people</th>
                    <th className="num">Automated</th>
                    <th className="num">Contacts</th>
                    <th className="num">Pending replies</th>
                    <th className="num">Median response</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.byLocation.map((row) => (
                    <tr key={row.locationId}>
                      <td className="nowrap">
                        <button
                          type="button"
                          className="link-button"
                          onClick={() => onFiltersChange({ ...filters, locationIds: [row.locationId], userIds: [] })}
                        >
                          {row.locationName ?? row.locationId}
                        </button>
                      </td>
                      <td className="num cell-strong">{formatNumber(row.total)}</td>
                      <td className="num">{formatNumber(row.inbound)}</td>
                      <td className="num">{formatNumber(row.person)}</td>
                      <td className="num">{formatNumber(row.automated)}</td>
                      <td className="num">{formatNumber(row.contacts)}</td>
                      <td className="num">{row.pending ? <strong>{formatNumber(row.pending)}</strong> : '0'}</td>
                      <td className="num">{formatSpan(row.medianResponseMs)}</td>
                    </tr>
                  ))}
                  {summary.byLocation.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="empty">
                        No messages in this range.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PendingTable({ rows, total }: { rows: PendingReply[]; total: number }) {
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Waiting</th>
            <th>Contact</th>
            <th>Sub-account</th>
            <th>Channel</th>
            <th>Latest message</th>
            <th className="num">Unanswered</th>
            <th>Last answered</th>
            <th>Handled by</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const hours = (Date.now() - row.firstWaitingAt) / 3_600_000;
            return (
              <tr key={row.contactId}>
                <td className="nowrap">
                  <span className={hours >= 24 ? 'wait wait-long' : hours >= 4 ? 'wait wait-mid' : 'wait'}>
                    {formatWait(row.firstWaitingAt)}
                  </span>
                  <div className="cell-sub">since {formatDateTime(row.firstWaitingAt)}</div>
                </td>
                <td>
                  {row.conversationUrl ? (
                    <a href={row.conversationUrl} target="_blank" rel="noreferrer">
                      {row.contactName ?? row.contactPhone ?? row.contactEmail ?? 'Unknown contact'}
                    </a>
                  ) : (
                    (row.contactName ?? row.contactPhone ?? 'Unknown contact')
                  )}
                  {row.contactName && row.contactPhone ? <div className="cell-sub">{row.contactPhone}</div> : null}
                </td>
                <td className="nowrap">{row.locationName ?? row.locationId}</td>
                <td className="nowrap">{CHANNEL_LABELS[row.channel] ?? row.channel}</td>
                <td className="message-cell">{row.lastMessage ?? <span className="muted">(no text)</span>}</td>
                <td className="num">{formatNumber(row.unanswered)}</td>
                <td className="nowrap">
                  {row.lastReplyAt ? formatDateTime(row.lastReplyAt) : <span className="muted">Never</span>}
                </td>
                <td>{row.handledBy ?? <span className="muted">Nobody yet</span>}</td>
              </tr>
            );
          })}
          {rows.length === 0 ? (
            <tr>
              <td colSpan={8} className="empty">
                Nobody is waiting for a reply.
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
      {total > rows.length ? (
        <p className="small muted">
          Showing the {formatNumber(rows.length)} longest waits of {formatNumber(total)}. Export the CSV for all of
          them.
        </p>
      ) : null}
    </div>
  );
}

function MessageTable({
  data,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
}: {
  data: MessagesResponse | null;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}) {
  const totalPages = data ? Math.max(1, Math.ceil(data.total / pageSize)) : 1;
  return (
    <>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>When</th>
              <th>Contact</th>
              <th>Sub-account</th>
              <th>Channel</th>
              <th>Direction</th>
              <th>Sent by</th>
              <th>Message</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {(data?.messages ?? []).map((message) => (
              <tr key={message.id}>
                <td className="nowrap">{formatDateTime(message.dateAdded)}</td>
                <td>
                  {message.conversationUrl ? (
                    <a href={message.conversationUrl} target="_blank" rel="noreferrer">
                      {message.contactName ?? message.contactPhone ?? 'Unknown contact'}
                    </a>
                  ) : (
                    (message.contactName ?? message.contactPhone ?? 'Unknown contact')
                  )}
                </td>
                <td className="nowrap">{message.locationName ?? message.locationId}</td>
                <td className="nowrap">{CHANNEL_LABELS[message.channel] ?? message.channel}</td>
                <td className="nowrap">{message.direction === 'inbound' ? 'Received' : 'Sent'}</td>
                <td>
                  {message.sender === 'contact' ? (
                    <span className="muted">Contact</span>
                  ) : (
                    (message.userName ??
                    (message.sender === 'automated' ? <span className="muted">Automation</span> : 'Team'))
                  )}
                  {message.sender === 'automated' && message.source ? (
                    <div className="cell-sub">{message.source}</div>
                  ) : null}
                </td>
                <td className="message-cell">{message.body ?? <span className="muted">(no text)</span>}</td>
                <td className="nowrap">{message.status ?? '—'}</td>
              </tr>
            ))}
            {data && data.messages.length === 0 ? (
              <tr>
                <td colSpan={8} className="empty">
                  No messages match these filters.
                </td>
              </tr>
            ) : null}
            {!data ? (
              <tr>
                <td colSpan={8} className="empty">
                  Loading…
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      <div className="pagination">
        <div className="row">
          <label htmlFor="messages-page-size" className="muted">
            Rows
          </label>
          <select
            id="messages-page-size"
            value={pageSize}
            onChange={(event) => onPageSizeChange(Number(event.target.value))}
          >
            {[25, 50, 100, 200].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
        <div className="row">
          <button type="button" className="btn btn-sm" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
            Previous
          </button>
          <span>
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            className="btn btn-sm"
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
          >
            Next
          </button>
        </div>
      </div>
    </>
  );
}
